import { http } from "@/shared/lib/api/http"
import {
  type CreateMinisterInput,
  type Minister,
  MinisterKind,
  MinisterStatus,
  type MinistersListQuery,
  type MinistersListResponse,
  type UpdateMinisterInput,
} from "@/features/church/config/ministers/types/minister.types"

type MinisterApiResponse = {
  id: string
  workspaceId: string
  branchId?: string | null
  ministryTypeId?: string | null
  kind?: string | null
  displayName: string
  title?: string | null
  avatarUrl?: string | null
  bio?: string | null
  phone?: string | null
  email?: string | null
  memberId?: string | null
  userId?: string | null
  status?: string | null
  notes?: string | null
  ministryType?: Minister["ministryType"]
  branch?: Minister["branch"]
  member?: Minister["member"]
  user?: Minister["user"]
  createdAt?: string
  updatedAt?: string
}

type MinisterListApiResponse = {
  items?: MinisterApiResponse[]
  total?: number
  page?: number
  limit?: number
}

function mapStatus(value: unknown): MinisterStatus {
  return value === "INACTIVE" ? MinisterStatus.INACTIVE : MinisterStatus.ACTIVE
}

function mapKind(value: unknown): MinisterKind {
  return value === "EXTERNAL" ? MinisterKind.EXTERNAL : MinisterKind.INTERNAL
}

function normalizeMinister(input: MinisterApiResponse): Minister {
  return {
    id: input.id,
    workspaceId: input.workspaceId,
    branchId: input.branchId ?? null,
    ministryTypeId: input.ministryTypeId ?? null,
    kind: mapKind(input.kind),
    displayName: input.displayName,
    title: input.title ?? null,
    avatarUrl: input.avatarUrl ?? null,
    bio: input.bio ?? null,
    phone: input.phone ?? null,
    email: input.email ?? null,
    memberId: input.memberId ?? null,
    userId: input.userId ?? null,
    status: mapStatus(input.status),
    notes: input.notes ?? null,
    ministryType: input.ministryType ?? null,
    branch: input.branch ?? null,
    member: input.member ?? null,
    user: input.user ?? null,
    createdAt: input.createdAt ?? new Date().toISOString(),
    updatedAt: input.updatedAt ?? input.createdAt ?? new Date().toISOString(),
  }
}

function toNullableString(value: string | null | undefined) {
  if (value === null || value === undefined) return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function buildListParams(query: MinistersListQuery) {
  const params: Record<string, string | number> = {}
  if (query.workspaceId) params.workspaceId = query.workspaceId
  if (query.branchId) params.branchId = query.branchId
  if (query.scope) params.scope = query.scope
  if (query.allBranches) params.allBranches = "true"
  if (query.kind) params.kind = query.kind
  if (query.ministryTypeId) params.ministryTypeId = query.ministryTypeId
  if (query.status) params.status = query.status
  const search = (query.search ?? query.q ?? "").trim()
  if (search) params.search = search
  if (typeof query.page === "number" && query.page > 0) params.page = query.page
  if (typeof query.limit === "number" && query.limit > 0) params.limit = query.limit
  if (query.sortBy) params.sortBy = query.sortBy
  if (query.sortOrder) params.sortOrder = query.sortOrder
  if (query.filters && query.filters.trim()) params.filters = query.filters.trim()
  return params
}

export async function listMinistersService(query: MinistersListQuery): Promise<MinistersListResponse> {
  const response = await http.get<MinisterListApiResponse>("/ministers", { params: buildListParams(query) })
  const items = Array.isArray(response.data.items) ? response.data.items.map(normalizeMinister) : []
  return {
    items,
    total: typeof response.data.total === "number" ? response.data.total : items.length,
    page: typeof response.data.page === "number" && response.data.page > 0 ? response.data.page : 1,
    limit:
      typeof response.data.limit === "number" && response.data.limit > 0
        ? response.data.limit
        : Math.max(items.length, 1),
  }
}

export async function getMinisterService(id: string): Promise<Minister> {
  const response = await http.get<MinisterApiResponse>(`/ministers/${id}`)
  return normalizeMinister(response.data)
}

export async function createMinisterService(input: CreateMinisterInput): Promise<Minister> {
  const payload: Record<string, unknown> = {
    kind: input.kind,
    displayName: input.displayName.trim(),
  }
  if (input.workspaceId) payload.workspaceId = input.workspaceId
  // INTERNAL ministers send branchId (the locked active branch); EXTERNAL ministers are workspace-wide.
  if (input.kind === MinisterKind.INTERNAL && input.branchId) {
    payload.branchId = input.branchId
  }
  if (input.title !== undefined) payload.title = toNullableString(input.title)
  if (input.bio !== undefined) payload.bio = toNullableString(input.bio)
  if (input.phone !== undefined) payload.phone = toNullableString(input.phone)
  if (input.email !== undefined) payload.email = toNullableString(input.email)
  if (input.notes !== undefined) payload.notes = toNullableString(input.notes)
  if (input.ministryTypeId !== undefined) payload.ministryTypeId = toNullableString(input.ministryTypeId)
  if (input.memberId !== undefined) payload.memberId = toNullableString(input.memberId)
  if (input.userId !== undefined) payload.userId = toNullableString(input.userId)
  if (input.status !== undefined) payload.status = input.status

  const response = await http.post<MinisterApiResponse>("/ministers", payload)
  return normalizeMinister(response.data)
}

export async function updateMinisterService(id: string, input: UpdateMinisterInput): Promise<Minister> {
  const payload: Record<string, unknown> = {}
  if (input.displayName !== undefined) payload.displayName = input.displayName.trim()
  if (input.title !== undefined) payload.title = toNullableString(input.title)
  if (input.bio !== undefined) payload.bio = toNullableString(input.bio)
  if (input.phone !== undefined) payload.phone = toNullableString(input.phone)
  if (input.email !== undefined) payload.email = toNullableString(input.email)
  if (input.notes !== undefined) payload.notes = toNullableString(input.notes)
  if (input.ministryTypeId !== undefined) payload.ministryTypeId = toNullableString(input.ministryTypeId)
  if (input.memberId !== undefined) payload.memberId = toNullableString(input.memberId)
  if (input.userId !== undefined) payload.userId = toNullableString(input.userId)
  if (input.status !== undefined) payload.status = input.status

  const response = await http.patch<MinisterApiResponse>(`/ministers/${id}`, payload)
  return normalizeMinister(response.data)
}

export async function deleteMinisterService(id: string): Promise<void> {
  await http.delete(`/ministers/${id}`)
}

/** Upload (or replace) a minister's standalone photo. The endpoint expects a multipart `file` field. */
export async function uploadMinisterPhotoService(id: string, file: File): Promise<Minister> {
  const formData = new FormData()
  formData.append("file", file)
  const response = await http.post<MinisterApiResponse>(`/ministers/${id}/photo`, formData)
  return normalizeMinister(response.data)
}
